import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [errorMessage, setErrorMessage] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setErrorMessage("Session introuvable");
      return setStatus("error");
    }
    let active = true;

    const poll = async (n = 0) => {
      if (!active) return;
      if (n >= 15) {
        // Last-chance reconciliation
        try {
          const { data } = await api.post("/payments/reconcile-mine");
          if (data.ticketsCreated?.length) {
            setStatus("paid");
            setTimeout(() => nav("/my-ticket"), 1200);
            return;
          }
        } catch {}
        setErrorMessage("La vérification a pris plus de temps que prévu. Votre banque confirme peut-être encore le paiement.");
        return setStatus("timeout");
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setTimeout(() => nav("/my-ticket"), 1200);
          return;
        }
        if (data.status === "expired") {
          setErrorMessage("La session de paiement a expiré.");
          return setStatus("expired");
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setErrorMessage("Une erreur est survenue pendant la vérification.");
        setStatus("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, nav]);

  return (
    <div className="container mx-auto px-4 py-20 max-w-lg text-center">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="mx-auto animate-spin text-brand" size={56} />
          <h1 className="mt-6 font-display text-3xl font-bold text-ink">Vérification du paiement…</h1>
          <p className="mt-3 text-subtle">Merci de patienter, nous confirmons votre paiement auprès de votre banque.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="mx-auto text-[#34C759]" size={64} />
          <h1 className="mt-6 font-display text-3xl font-black text-ink">Paiement validé</h1>
          <p className="mt-2 text-subtle">Votre ticket a été créé. Redirection…</p>
          <Button onClick={() => nav("/my-ticket")} className="mt-8 h-12 bg-brand hover:bg-brand-hover text-white rounded-md px-8" data-testid="go-to-ticket-btn">
            Voir mon ticket
          </Button>
        </div>
      )}
      {(status === "error" || status === "expired" || status === "timeout") && (
        <div data-testid="payment-error">
          <XCircle className="mx-auto text-destructive" size={64} />
          <h1 className="mt-6 font-display text-3xl font-black text-ink">
            {status === "timeout" ? "Confirmation en attente" : status === "expired" ? "Session expirée" : "Paiement non abouti"}
          </h1>
          <p className="mt-3 text-subtle max-w-md mx-auto">
            {errorMessage || "Votre paiement n'a pas pu être finalisé. Aucune somme n'a été débitée si vous ne voyez pas de ticket."}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => nav("/my-ticket")} variant="outline" className="h-12 rounded-md px-8 border border-ink">
              Voir mes tickets
            </Button>
            <Button onClick={() => nav("/")} className="h-12 bg-ink hover:bg-brand text-white rounded-md px-8">
              Retour à l'accueil
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
